/* ––
 * ––– Imports
 * –––––––––––––––––––––––––––––––– */
import {
  useState, useCallback, useMemo, useEffect,
} from 'react';

import { Map } from 'immutable';
import isEmpty from 'lodash/isEmpty';

import useBlobRecord from 'helpers/direct_upload/blob_record';

/* ––
 * ––– Constants
 * –––––––––––––––––––––––––––––––– */
const initialState = new Map({
  sourceResponse:    null,
  workingBlobs:      [],
  processedResponse: null,
  processError:      null,
  processing:        false,
});

/* ––
 * ––– Hook
 * –––––––––––––––––––––––––––––––– */
function useBlobResponseHandler() {
  /* –– State declaration
   * –––––––––––––––––––––––––––––––– */
  const [state, setState] = useState(initialState);
  const sourceResponse    = state.get('sourceResponse');
  const processedResponse = state.get('processedResponse');
  const workingBlobs    = state.get('workingBlobs');
  const processError    = state.get('processError');
  const processing      = state.get('processing');

  const [, uploadData, uploadError] = useBlobRecord(workingBlobs, 'response');

  /* –– Handlers
   * –––––––––––––––––––––––––––––––– */
  const processBlobResponse = useCallback((response) => {
    if (processing) {
      return [false, 'There can be only one processing response at a time'];
    }

    if (isEmpty(response) || !response.get('blob')) {
      return [false, 'Provided response is not a blob response'];
    }

    setState(
      initialState
        .set('sourceResponse', response)
        .set('processing', true)
        .set('workingBlobs', [...response.get('blob')]),
    );

    return [true, null];
  }, [processing]);

  /* –– Effects
   * –––––––––––––––––––––––––––––––– */
  useEffect(() => {
    if (isEmpty(uploadError)) return;

    const errorMessage = t('eos.landing_page.text.upload_failed');

    setState(
      (previousState) => previousState
        .set('processing', false)
        .set('processError', errorMessage)
        .set('processedResponse', null),
    );
  }, [uploadError]);

  useEffect(() => {
    if (isEmpty(uploadData)) return;

    const response = new Map(sourceResponse).set('file', uploadData[0].signedBlobId);

    setState(
      (previousState) => previousState
        .set('processing', false)
        .set('processError', null)
        .set('processedResponse', response),
    );
  }, [uploadData]);

  /* –– Hook exports
   * –––––––––––––––––––––––––––––––– */
  return useMemo(
    () => [sourceResponse, processedResponse, processing, processError, processBlobResponse],
    [sourceResponse, processedResponse, processing, processError, processBlobResponse],
  );
}

/* ––
 * ––– Exports
 * –––––––––––––––––––––––––––––––– */
export default useBlobResponseHandler;
